import { useEffect, useState } from "react";
import { Warehouse, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const EMPTY = { address_title: "", contact_name: "", phone: "", email: "", address_line_1: "", address_line_2: "", pincode: "", city: "", state: "" };

export default function Warehouses() {
  const [list, setList] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => appApi.get("warehouses").then((d) => setList(d.warehouses || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await appApi.post("warehouses", { ...form, name: form.contact_name });
      toast.success("Warehouse added — syncing with courier network.");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="warehouses-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Warehouses</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Pickup addresses</h1>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          data-testid="warehouse-add-toggle"
          className="inline-flex items-center gap-2 bg-acid text-white text-sm font-bold px-5 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showForm ? "Cancel" : "Add Warehouse"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="warehouse-form" className="border-2 border-obsidian bg-white p-6 grid sm:grid-cols-3 gap-4">
          {error && <div data-testid="warehouse-error" className="sm:col-span-3 border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{error}</div>}
          {[
            { k: "address_title", label: "Title", ph: "Main warehouse", req: true },
            { k: "contact_name", label: "Contact person", ph: "Sourav", req: true },
            { k: "phone", label: "Phone (10 digits)", ph: "9876543210", req: true },
            { k: "email", label: "Email", ph: "warehouse@brand.in", req: false },
            { k: "address_line_1", label: "Address line 1", ph: "Plot 12, Industrial Rd", req: true },
            { k: "address_line_2", label: "Address line 2", ph: "Near station", req: false },
            { k: "pincode", label: "Pincode", ph: "713101", req: true },
            { k: "city", label: "City", ph: "Burdwan", req: true },
            { k: "state", label: "State", ph: "West Bengal", req: true },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500" htmlFor={`wh-${f.k}`}>{f.label}</label>
              <input id={`wh-${f.k}`} data-testid={`wh-${f.k}-input`} required={f.req} value={form[f.k]} onChange={set(f.k)} placeholder={f.ph}
                maxLength={f.k === "pincode" ? 6 : f.k === "phone" ? 10 : undefined}
                className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
            </div>
          ))}
          <div className="sm:col-span-3">
            <button type="submit" data-testid="warehouse-submit" disabled={saving}
              className="bg-obsidian text-sky-300 font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(10,10,10,0.35)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
              {saving ? "Saving…" : "Save Warehouse"}
            </button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="warehouses-list">
        {(list || []).map((w) => (
          <div key={w.id} className="border-2 border-obsidian bg-white p-6 hover:shadow-[6px_6px_0px_0px_#0A0A0A] transition-all duration-150" data-testid={`warehouse-card-${w.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><Warehouse className="w-5 h-5" /></span>
                <div>
                  <p className="font-display font-extrabold tracking-tight">{w.address_title}</p>
                  <p className="text-xs font-mono text-zinc-500">{w.contact_name || w.name} · {w.phone}</p>
                </div>
              </div>
              <div className="flex gap-1.5">
                <StatusBadge status={w.status} />
                {w.sync_status && <StatusBadge status={w.sync_status} />}
              </div>
            </div>
            <p className="text-sm text-zinc-600 mt-4">{w.address_line_1}{w.address_line_2 ? `, ${w.address_line_2}` : ""}</p>
            <p className="text-xs font-mono text-zinc-500 mt-1">{w.city}, {w.state} — {w.pincode}</p>
            {w.is_default && <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-blaze mt-3">Default pickup</p>}
          </div>
        ))}
        {list && list.length === 0 && (
          <p className="text-sm text-zinc-500 md:col-span-2" data-testid="warehouses-empty">No warehouses yet. Add a pickup address to start booking B2C shipments.</p>
        )}
      </div>
    </div>
  );
}
